import React from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { IconPicker } from '@/components/ui/icon-picker'
import { Textarea } from '@/components/ui/textarea'
import { Users, Lock, Shield } from 'lucide-react'
import { cn } from '@/lib/utils'
import { generateSlug, typedSlug } from '@/lib/slug'
import { useCommunity } from '@/lib/CommunityContext'
import { VisibilityPicker } from '@/components/ui/visibility-picker'
import { AccessGrantPicker } from '@/components/ui/access-grant-picker'
import { LockedDisplayPicker } from '@/components/ui/locked-display-picker'
import {
  emptyAccessGrants,
  grantCount,
  toAccessGrantInput,
  type AccessGrants,
  type Visibility
} from '@/types/access'
import type { CommonSpaceData } from '@/types/space-wizard'


interface CommonSpaceFieldsProps {
  data: CommonSpaceData
  onChange: (data: CommonSpaceData) => void
  disabled?: boolean
  /** The section this space will live in — it pins the floor for the visibility. */
  sectionVisibility?: Visibility
  sectionTitle?: string
  /** The section is administration only (staff's `adminOnly`), and so is every space in it. */
  sectionAdminOnly?: boolean
  /** Off for modules with their own rule instead, like "who issues" in certificates. */
  showWhoCanPost?: boolean
  /**
   * Takes the "who can post" slot, below the visibility and its access groups, for a module whose
   * rule replaces it ("who issues" in certificates): same place, same layout.
   */
  whoCanPostSlot?: React.ReactNode
  /** The API's reason for refusing the slug, shown under the field. */
  slugError?: string | null
}

export const CommonSpaceFields: React.FC<CommonSpaceFieldsProps> = ({
  data,
  onChange,
  disabled = false,
  sectionVisibility,
  sectionTitle,
  sectionAdminOnly = false,
  showWhoCanPost = true,
  whoCanPostSlot,
  slugError = null
}) => {
  const { t } = useTranslation(['common'])
  const { community } = useCommunity()

  /* The wizard's `data` carries the ids the API takes; the picker works in the
     objects it draws. This holds the drawn form and pushes the ids down. */
  const [access, setAccess] = React.useState<AccessGrants>(emptyAccessGrants())

  const updateAccess = (next: AccessGrants) => {
    setAccess(next)
    onChange({ ...data, access: toAccessGrantInput(next) })
  }

  const handleTitleChange = (value: string) => {
    onChange({
      ...data,
      title: value,
      slug: !data.slug || data.slug === generateSlug(data.title) ? generateSlug(value) : data.slug
    })
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-[60px_1fr] gap-2">
        <div className="space-y-2">
          <label htmlFor="icon" className="text-sm font-medium text-gray-700">
            {t('common:fields.icon')}
          </label>
          <IconPicker
            value={data.icon}
            onChange={(icon) => onChange({ ...data, icon })}
            disabled={disabled}
            id="icon"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="title" className="text-sm font-medium text-gray-700">
            {t('common:fields.title')} <span className="text-red-500">*</span>
          </label>
          <Input
            id="title"
            value={data.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder={t('common:spaces.titlePlaceholder')}
            disabled={disabled}
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="slug" className="text-sm font-medium text-gray-700">
          {t('common:fields.slug')}
        </label>
        <Input
          id="slug"
          value={data.slug}
          onChange={(e) => onChange({ ...data, slug: typedSlug(e.target.value) })}
          placeholder={t('common:spaces.slugPlaceholder')}
          disabled={disabled}
          aria-invalid={!!slugError}
          className={slugError ? 'border-red-500 focus-visible:ring-red-500/20' : undefined}
        />
        {slugError ? (
          <p className="text-xs text-red-600">{slugError}</p>
        ) : (
          <p className="text-xs text-gray-500">
            {t('common:spaces.slugHint')}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <label htmlFor="description" className="text-sm font-medium text-gray-700">
          {t('common:fields.description')}
        </label>
        <Textarea aiFieldKind="other"
          id="description"
          value={data.description}
          onChange={(e) => onChange({ ...data, description: e.target.value })}
          placeholder={t('common:spaces.descriptionPlaceholder')}
          disabled={disabled}
          rows={3}
        />
      </div>

      <VisibilityPicker
        value={data.visibility}
        onChange={(visibility) => onChange({ ...data, visibility })}
        disabled={disabled}
        sectionVisibility={sectionVisibility}
        sectionTitle={sectionTitle}
      />

      {data.visibility === 'private' && (
        <div className="rounded-lg border p-3">
          <AccessGrantPicker
            value={access}
            onChange={updateAccess}
            communityId={community?.id}
            disabled={disabled}
          />
        </div>
      )}

      {/* F-161a: a public space keeps nobody out. A private space with nobody listed inside a
          private section takes the section's audience, so it is not administration only. */}
      {data.visibility !== 'public' && (
        <LockedDisplayPicker
          kind="space"
          value={data.lockedDisplay ?? 'inherit'}
          onChange={(lockedDisplay) => onChange({ ...data, lockedDisplay })}
          disabled={disabled}
          adminOnly={sectionAdminOnly || (data.visibility === 'private' && grantCount(access) === 0 && sectionVisibility !== 'private')}
        />
      )}

      {/* Below the visibility and its access groups: who may post is a separate decision. */}
      {whoCanPostSlot ?? (showWhoCanPost && <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700">
          {t('common:modules.settings.whoCanPost')}
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange({ ...data, whoCanPost: 'anyone' })}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm whitespace-nowrap",
              (data.whoCanPost || 'anyone') === 'anyone'
                ? "bg-primary-100 text-primary-700"
                : "hover:bg-gray-100",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            title={t('common:modules.settings.whoCanPostAnyone')}
          >
            <Users className="w-4 h-4 flex-shrink-0" />
            <span>{t('common:modules.settings.whoCanPostAnyone')}</span>
          </button>

          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange({ ...data, whoCanPost: 'staff' })}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm whitespace-nowrap",
              data.whoCanPost === 'staff'
                ? "bg-primary-100 text-primary-700"
                : "hover:bg-gray-100",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            title={t('common:modules.settings.whoCanPostStaffHint')}
          >
            <Shield className="w-4 h-4 flex-shrink-0" />
            <span>{t('common:modules.settings.whoCanPostStaff')}</span>
          </button>

          <button
            type="button"
            disabled={disabled}
            onClick={() => onChange({ ...data, whoCanPost: 'admins' })}
            className={cn(
              "flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm whitespace-nowrap",
              data.whoCanPost === 'admins'
                ? "bg-primary-100 text-primary-700"
                : "hover:bg-gray-100",
              disabled && "opacity-50 cursor-not-allowed"
            )}
            title={t('common:modules.settings.whoCanPostAdminsHint')}
          >
            <Lock className="w-4 h-4 flex-shrink-0" />
            <span>{t('common:modules.settings.whoCanPostAdmins')}</span>
          </button>
        </div>
      </div>)}
    </div>
  )
}
